import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  PencilIcon,
  UserIcon,
  CollectionIcon,
  MinusCircleIcon,
  CalendarIcon,
  TableIcon,
} from "@heroicons/react/outline";
import SlideOver from "../../Modal/SlideOver";
import ExpandableDescription from "../ExpandableDescription";
import Button from "../../Button";
import { currencyFormat } from "../../../../Utils";
import { BANKS } from "../../../../Constants";
import { RISK_LEVEL_OPTIONS } from "../../../../Constants/business";

const EMPLOYMENT_TYPE_STYLES = {
  PERMANENT: { badge: "bg-blue-100 text-blue-800", key: "permanent" },
  CONTRACT: { badge: "bg-purple-100 text-purple-800", key: "contract" },
  PROBATION: { badge: "bg-yellow-100 text-yellow-800", key: "probation" },
  DAILY_WORKER: { badge: "bg-orange-100 text-orange-800", key: "daily" },
  INTERN: { badge: "bg-cyan-100 text-cyan-800", key: "intern" },
  FREELANCE: { badge: "bg-pink-100 text-pink-800", key: "freelance" },
  PART_TIME: { badge: "bg-indigo-100 text-indigo-800", key: "partTime" },
};

const EMPLOYMENT_STATUS_STYLES = {
  ACTIVE: { badge: "bg-green-100 text-green-800", key: "active" },
  LEAVE: { badge: "bg-yellow-100 text-yellow-800", key: "leave" },
  RESIGNED: { badge: "bg-red-100 text-red-800", key: "resigned" },
  TERMINATED: { badge: "bg-gray-100 text-gray-800", key: "terminated" },
};

// Marriage status values are TK / K, not single / married
const MARRIAGE_STATUS_KEY = { TK: "single", K: "married" };

// Tax method values are GROSS / GROSS_UP
const TAX_METHOD_KEY = { GROSS: "gross", GROSS_UP: "grossUp" };

// ID type values are KTP / PASSPORT
const ID_TYPE_KEY = { KTP: "idCard", PASSPORT: "passport" };

// Bank name is saved as the bank code ("014"); older records hold the BANKS id.
// Labelled the same way as the bank picker in EmployeeModal.
const getBankLabel = (bankName) => {
  if (!bankName) return "-";
  const bank = BANKS.find(
    (item) =>
      item.value === bankName || String(item.id) === String(bankName),
  );
  return bank
    ? `${bank.shortLabel} (${bank.code}) - ${bank.abbreviation}`
    : bankName;
};

// Work times are saved as "HH:mm:ss"
const formatWorkTime = (time) => (time ? time.slice(0, 5) : "-");

const EmployeeDetailModal = ({
  open,
  onClose,
  onEdit,
  onManageAllowance,
  onManageDeduction,
  onViewAttendance,
  onViewSalaryTable,
  employee,
}) => {
  const { t, i18n } = useTranslation();

  if (!employee) return null;

  const fullName =
    `${employee.firstName || ""} ${employee.lastName || ""}`.trim() || "-";

  const typeStyle =
    EMPLOYMENT_TYPE_STYLES[employee.employmentType] ||
    EMPLOYMENT_TYPE_STYLES.PERMANENT;
  const statusStyle =
    EMPLOYMENT_STATUS_STYLES[employee.employmentStatus] ||
    EMPLOYMENT_STATUS_STYLES.ACTIVE;
  const riskLevel = RISK_LEVEL_OPTIONS.find(
    (option) => option.value === employee.riskLevel,
  );

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={t("employees.detail.title")}
      actionButton={
        onEdit && (
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
            onClick={() => onEdit(employee)}
          >
            <PencilIcon className="h-5 w-5" />
          </button>
        )
      }
    >
      <div className="space-y-6 mb-6">
        {/* Header Summary */}
        <div className="bg-white px-4 py-5 shadow sm:rounded-lg sm:p-6 border border-gray-100">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center">
                <UserIcon className="h-7 w-7 text-gray-500" />
              </div>
              <div>
                <ExpandableDescription
                  text={fullName}
                  maxLength={40}
                  textClassName="text-xl font-semibold text-gray-900"
                />
                {employee.employeeID && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    {employee.employeeID}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-3">
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${typeStyle.badge}`}
              >
                {t(`employees.employmentType.${typeStyle.key}`)}
              </span>
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusStyle.badge}`}
              >
                {t(`employees.employmentStatus.${statusStyle.key}`)}
              </span>
            </div>
          </div>

          {/* Job details */}
          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 mt-4 pt-4 border-t border-gray-200">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.position")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.position || "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.department")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.department || "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.startDate")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.startDate
                  ? dayjs(employee.startDate)
                      .locale(i18n.language)
                      .format("DD MMM YYYY")
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.endDate")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.endDate
                  ? dayjs(employee.endDate)
                      .locale(i18n.language)
                      .format("DD MMM YYYY")
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.riskLevel")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {riskLevel ? t(riskLevel.label) : employee.riskLevel || "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.minimumWorkHoursPerDay")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.minimumWorkHoursPerDay != null
                  ? employee.minimumWorkHoursPerDay
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.standardWorkStartTime")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatWorkTime(employee.standardWorkStartTime)}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.standardWorkEndTime")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatWorkTime(employee.standardWorkEndTime)}
              </dd>
            </div>
          </dl>
        </div>

        {/* Personal Info */}
        <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">
              {t("employees.modal.steps.personal")}
            </h3>
          </div>
          <dl className="px-4 py-4 sm:px-6 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.gender")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.gender
                  ? t(`employees.gender.${employee.gender.toLowerCase()}`)
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.birthDate")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.birthDate
                  ? dayjs(employee.birthDate)
                      .locale(i18n.language)
                      .format("DD MMM YYYY")
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.birthPlace")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.birthPlace || "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.marriageStatus")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.marriageStatus
                  ? t(
                      `employees.marriageStatus.${
                        MARRIAGE_STATUS_KEY[employee.marriageStatus] ??
                        employee.marriageStatus.toLowerCase()
                      }`,
                    )
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.dependent")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.dependent != null ? employee.dependent : "-"}
              </dd>
            </div>
          </dl>
        </div>

        {/* Contact Info */}
        <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">
              {t("employees.fields.contact")}
            </h3>
          </div>
          <dl className="px-4 py-4 sm:px-6 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.email")}
              </dt>
              <dd className="mt-1 text-sm">
                {employee.email ? (
                  <a
                    href={`mailto:${employee.email}`}
                    className="text-blue-600 hover:underline"
                  >
                    {employee.email}
                  </a>
                ) : (
                  <span className="text-gray-900">-</span>
                )}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.phone")}
              </dt>
              <dd className="mt-1 text-sm">
                {employee.phone ? (
                  <a
                    href={`tel:${employee.phone}`}
                    className="text-blue-600 hover:underline"
                  >
                    {employee.phone}
                  </a>
                ) : (
                  <span className="text-gray-900">-</span>
                )}
              </dd>
            </div>

            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.address")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.address ? (
                  <ExpandableDescription
                    text={employee.address}
                    maxLength={80}
                    textClassName="text-gray-900"
                  />
                ) : (
                  "-"
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* Identity */}
        <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">
              {t("employees.modal.steps.identity")}
            </h3>
          </div>
          <dl className="px-4 py-4 sm:px-6 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.idType")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.idType
                  ? t(
                      `employees.idType.${
                        ID_TYPE_KEY[employee.idType] ??
                        employee.idType.toLowerCase()
                      }`,
                    )
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.idNumber")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.idNumber || "-"}
              </dd>
            </div>

            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.npwp")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.npwp || "-"}
              </dd>
            </div>
          </dl>
        </div>

        {/* Salary Info */}
        <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">
              {t("employees.salary.title")}
            </h3>
          </div>
          <dl className="px-4 py-4 sm:px-6 grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.salaryType")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.salaryType
                  ? t(
                      `employees.salary.type.${employee.salaryType.toLowerCase()}`,
                    )
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.baseSalary")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.baseSalary != null
                  ? currencyFormat(employee.baseSalary)
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.taxMethod")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.taxMethod
                  ? t(
                      `employees.taxMethod.${
                        TAX_METHOD_KEY[employee.taxMethod] ??
                        employee.taxMethod.toLowerCase()
                      }`,
                    )
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.taxSubjectStartDate")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.taxSubjectStartDate
                  ? dayjs(employee.taxSubjectStartDate)
                      .locale(i18n.language)
                      .format("DD MMM YYYY")
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.taxSubjectEndDate")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.taxSubjectEndDate
                  ? dayjs(employee.taxSubjectEndDate)
                      .locale(i18n.language)
                      .format("DD MMM YYYY")
                  : "-"}
              </dd>
            </div>

            {(employee.taxSubjectStartDate || employee.taxSubjectEndDate) && (
              <div className="sm:col-span-2">
                <p className="text-sm text-gray-500">
                  {t("employees.fields.taxSubjectAnnualisedHelp")}
                </p>
              </div>
            )}

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.paymentMethod")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.paymentMethod
                  ? t(
                      `employees.salary.method.${
                        employee.paymentMethod === "BANK_TRANSFER"
                          ? "bankTransfer"
                          : employee.paymentMethod.toLowerCase()
                      }`,
                    )
                  : "-"}
              </dd>
            </div>

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("employees.fields.paymentPeriod")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {employee.paymentPeriod
                  ? t(
                      `employees.salary.period.${
                        employee.paymentPeriod === "TWICE_MONTHLY"
                          ? "twiceMonthly"
                          : employee.paymentPeriod.toLowerCase()
                      }`,
                    )
                  : "-"}
              </dd>
            </div>

            {/* Bank Info — only if payment method is bank transfer */}
            {employee.paymentMethod === "BANK_TRANSFER" &&
              employee.bankAccount && (
                <>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("employees.fields.bankName")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {getBankLabel(employee.bankAccount.bankName)}
                    </dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("employees.fields.bankBranch")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {employee.bankAccount.bankBranch || "-"}
                    </dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("employees.fields.accountNumber")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {employee.bankAccount.accountNumber || "-"}
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("employees.fields.accountHolderName")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {employee.bankAccount.accountHolderName || "-"}
                    </dd>
                  </div>
                </>
              )}
          </dl>
        </div>

        {/* Quick Actions */}
        {(onManageAllowance ||
          onManageDeduction ||
          onViewAttendance ||
          onViewSalaryTable) && (
          <div className="flex flex-col gap-3">
            {onViewAttendance && (
              <Button
                variant="secondary"
                color="blue"
                className="w-full justify-center"
                onClick={() => onViewAttendance(employee)}
                icon={<CalendarIcon className="h-4 w-4" />}
              >
                {t("employees.attendances.label")}
              </Button>
            )}
            {onViewSalaryTable && (
              <Button
                variant="secondary"
                color="blue"
                className="w-full justify-center"
                onClick={() => onViewSalaryTable(employee)}
                icon={<TableIcon className="h-4 w-4" />}
              >
                {t("employees.salaries.viewSalaryTable")}
              </Button>
            )}
            {onManageAllowance && (
              <Button
                variant="secondary"
                color="blue"
                className="w-full justify-center"
                onClick={() => onManageAllowance(employee)}
                icon={<CollectionIcon className="h-4 w-4" />}
              >
                {t("employees.allowances.label")}
              </Button>
            )}
            {onManageDeduction && (
              <Button
                variant="secondary"
                color="blue"
                className="w-full justify-center"
                onClick={() => onManageDeduction(employee)}
                icon={<MinusCircleIcon className="h-4 w-4" />}
              >
                {t("employees.deductions.label")}
              </Button>
            )}
          </div>
        )}
      </div>
    </SlideOver>
  );
};

export default EmployeeDetailModal;
